import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6"

import { EditUserManagementCentreWrapper } from "./styles"
import Header from "./components/header"
import FormInputsNewMember from "./components/formInputsNewMember"
import FormInputsServiceProvider from "./components/formInputsServiceProvider"
import { useContext, useEffect, useState } from "react"
import {
	APIS_KEYS,
	useGetUserTeamMember,
	usePutUpdateTeamMember,
} from "@services"
import { useQueryClient } from "@tanstack/react-query"
import { ToastMessageContext } from "@context/toast-message-cotnext"
import { formatPhoneForBackend, toE164 } from "@utils"
interface IEditUserManagementCentreProps {
	onclose: () => void
	selectedRecord:
		| {
				userId: number
				userTypeId: number
		  }
		| undefined
}
interface IFormUserManagementCentreNewMember {
	userType: number
	email: string
	roleId: number
	fullName: string
	phoneNumber: string
}

export interface IFormUserManagementCentreServiceProvider {
	selectedServices: {
		id: number
		name: string
		nameAr: string
		isSelected: boolean
	}[]
	fullName: string
	phoneNumber: string
}

export type IFormEditUserManagementCentre =
	| IFormUserManagementCentreNewMember
	| IFormUserManagementCentreServiceProvider

export default function EditUserManagementCentre({
	onclose,
	selectedRecord,
}: IEditUserManagementCentreProps) {
	/* ------------------------------- Local State ------------------------------ */
	const [activeTab, setActiveTab] = useState<2 | 3>()
	/* ---------------------------------- API'S --------------------------------- */
	const { data: getUserTeamMember } = useGetUserTeamMember({
		userId: selectedRecord?.userId.toString()!,
		userTypeId: selectedRecord?.userTypeId.toString()!,
	})
	const { mutateAsync: putUpdateTeamMember } = usePutUpdateTeamMember()
	/* ---------------------------------- Hooks --------------------------------- */
	const { showSuccess } = useContext(ToastMessageContext)
	const {
		t,
		i18n: { language },
	} = useTranslation()
	const queryClient = useQueryClient()
	const methods = useForm<IFormEditUserManagementCentre>({
		mode: "onChange", // Enable real-time validation
		reValidateMode: "onChange"
	})
	const { setValue } = methods
	const onSubmit = async (data: any) => {
		const newval =
			selectedRecord?.userTypeId === 3 &&
			data.selectedServices.filter((item: any) => item.isSelected && item.id)

		const teamMember = {
			email: data.email,
			roleId: data.roleId,
		}
		const provider = {
			selectedServices:
				selectedRecord?.userTypeId === 3
					? newval.map((item: any) => item.id)
					: [],
		}
		const commonData = {
			fullName: data.fullName,
			phoneNumber: formatPhoneForBackend(data.phoneNumber, "e164"),
			userTypeId: selectedRecord?.userTypeId,
			id: selectedRecord?.userId,
		}
		const collectData = selectedRecord?.userTypeId === 3 ? provider : teamMember
		const allData = { ...commonData, ...collectData }
		const res = await putUpdateTeamMember(allData as any)
		if (res.status === 200) {
			queryClient.invalidateQueries({
				queryKey: [APIS_KEYS.GETALLOWNERUSERS],
			})
			showSuccess(t("UserManagement.user_details_updated_successfully"))
			onclose()
		}
	}

	useEffect(() => {
		if (!selectedRecord) {
			onclose()
		}
		if (selectedRecord) {
			setActiveTab(selectedRecord?.userTypeId as 2 | 3)
		}
	}, [selectedRecord])

	useEffect(() => {
		setValue("fullName", getUserTeamMember?.result.fullName ?? "")
		setValue("phoneNumber", toE164(getUserTeamMember?.result.phoneNumber) ?? "")
		if (selectedRecord?.userTypeId === 2) {
			setValue("email", getUserTeamMember?.result.email ?? "")
			setValue("roleId", getUserTeamMember?.result.roleId ?? 0)
		}
		if (selectedRecord?.userTypeId === 3) {
			setValue(
				"selectedServices",
				getUserTeamMember?.result?.selectedServices ?? []
			)
		}
	}, [getUserTeamMember])
	return (
		<EditUserManagementCentreWrapper $lang={language as "ar" | "en"}>
			<div className='backAction'>
				<div onClick={onclose}>
					{language === "ar" ? <FaArrowLeftLong /> : <FaArrowRightLong />}
				</div>
			</div>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<div className='containerFormElements'>
						<Header />
						{activeTab === 2 && <FormInputsNewMember />}
						{activeTab === 3 && <FormInputsServiceProvider />}
					</div>
				</form>
			</FormProvider>
		</EditUserManagementCentreWrapper>
	)
}
